"use client";
import Image from "next/image";
import Link from "next/link";
import { useAppSelector } from "@/lib/hooks";
import {
  createQueryString,
  formatDate,
  getMyRoomSummary,
} from "@/utils/helper";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, useCallback, useMemo, Suspense, lazy } from "react";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import { Button, Modal, ModalHeader } from "reactstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { StarRating } from "@/components/common/StarRating";
import { ImageWithLoader } from "@/components/ui";
import { loading } from "@/lib/features/hotel/hotelSlice";
import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SELECT_ITEM_EVENT, trackEvent } from "@/utils/gtm";

// Lazy load these components
const ModalComponent = lazy(() => import("reactstrap").then(mod => ({ default: mod.Modal })));
const CancellationPolicy = lazy(() => import("../hotel-single/CancellationPolicy"));
const OtherInclusions = lazy(() => import("@/components/common/OtherInclusions"));


interface HotelCardProps {
  hotels: any;
  onHover?: (id: number | null) => void;
  onClick?: (data: any) => void;
}

// Skeleton Card Component matching HotelCard structure
const SkeletonCard = () => (
  <div className="col-12">
    <div className="border-top-light pt-30">
      <div className="row x-gap-20 y-gap-20">
        <div className="col-md-auto">
          <div className="cardImage ratio ratio-1:1 w-250 md:w-1/1 borderradius-40">
            <div className="cardImage__content" style={{ margin: "-7px 0px 0px 0px" }}>
              <Skeleton height={250}  className="borderradius-40" />
            </div>
          </div>
        </div>

        <div className="col-md">
          <h3 className="text-18 lh-16 fw-500">
            <Skeleton width={200} height={24} />
            <br className="lg:d-none" />
            <Skeleton width={100} height={16} />
          </h3>

          <div className="row x-gap-10 y-gap-10 items-center pt-10">
            <div className="col-auto">
              <Skeleton width={80} height={14} />
            </div>
            <div className="col-auto">
              <Skeleton width={100} height={14} />
            </div>
          </div>

          <div className="text-14 lh-15 mt-20">
            <div className="fw-500">
              <Skeleton width={120} height={14} />
            </div>
            <div className="text-light-1">
              <Skeleton width={180} height={14} />
            </div>
          </div>

          <div className="text-12 text-green-2 lh-15 mt-10">
            <div className="fw-500">
              <Skeleton width={140} height={12} />
            </div>
          </div>
        </div>

        <div className="col-md-auto text-right md:text-left">
          <div className="row x-gap-10 y-gap-10 justify-end items-center md:justify-start">
            <div className="col-auto">
              <Skeleton width={100} height={20} />
            </div>
            <div className="col-auto">
              <Skeleton width={40} height={40} circle={true} />
            </div>
          </div>

          <div>
            <div className="text-14 text-light-1 mt-50 md:mt-20">
              <Skeleton width={120} height={14} />
            </div>
            <div className="text-22 lh-12 fw-600 mt-5">
              <Skeleton width={80} height={22} />
            </div>
            <div className="text-14 text-light-1 mt-5">
              <Skeleton width={140} height={14} />
            </div>
            <Skeleton width="100%" height={40} className="mt-24" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HotelCard =({ hotels, onHover, onClick }: HotelCardProps) => {
  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const searchParams = useSearchParams();
  const router = useRouter();
  const isLoading = useAppSelector(loading);

  const response = hotels?.hotels || [];
  const {
    no_of_nights,
    no_of_adults,
    no_availability,
    no_of_children,
    no_of_infants,
    remaining_batches,
  } = hotels || {};

  const payloadFromQuery = useMemo(() => {
    return searchParams ? parseQueryString(searchParams) : null;
  }, [searchParams]);

  const toggleModal = useCallback((data: any = null) => {
    setModalData(data);
    setModal(prev => !prev);
  }, []);

  const handleHotelClick = useCallback((data: any) => {
    // console.log("item =>>>>", data);
    const itemsData = {
      item_id: data?.hotel_code,
      item_name: data?.name,
      price: data?.min_rate?.price,
      markup: data?.markup,  
      index:0,
      item_category: data?.accommodation_type_sub_name || "",
      location_id: data?.city_code,
    };
    
    const gtmData = {
      ecommerce: {
        item_list_id: "select_item",
        item_list_name: "Select Hotel",
        items: itemsData
      }
    };
    // console.log("gtmData select_item for ga4", gtmData);
    trackEvent(SELECT_ITEM_EVENT, gtmData);
  }, []);

  const redirectUrl = useCallback(
    (hotel_code: string, search_id: string) => {
      const payload = payloadFromQuery;
      const req: any = { ...payload, hotel_code, search_id };
      const queryString = makeQueryString(req);
      return `/hotel?${queryString}`;
    },
    [ payloadFromQuery, hotels]
  );

  const roomSummary = useMemo(() => getMyRoomSummary(hotels), [hotels]);

  const renderHotelItem = useCallback((item: any, index: number) => (
    <div className="col-12 child" key={item?.hotel_code}>
      <div className="border-top-light pt-30">
        <div className="row x-gap-20 y-gap-20">
          <div className="col-md-auto">
            <div className="cardImage ratio ratio-1:1 w-250 md:w-1/1 borderradius-40">
              <div className="cardImage__content" >
                <ImageWithLoader
                  width={400}
                  height={400}
                  src={item?.images?.url}
                  alt={`${item?.name} image`}
                  className="w-100 sm:cardImage__mobile"
                  loading="lazy"
                  parentClassName="cardImage__content custom_inside-slider"
                  unoptimized={true}
                />
              </div>
            </div>
          </div>

          <div className="col-md">
            <h3 className="text-18 lh-16 fw-500">
            <Link onClick={() => handleHotelClick(item)} href={`${redirectUrl(item?.hotel_code, item?.search_id)}`}>{item?.name}</Link>
              <br className="lg:d-none" />
              <StarRating value={item?.category} />
            </h3>

            <div className="row x-gap-10 y-gap-10 items-center pt-10">
              {item?.tripadvisor_data?.ranking_data && (
                <div className="col-auto">
                  <p className="text-14 fw-500">
                    {`#${item?.tripadvisor_data?.ranking_data?.ranking} in`}
                  </p>
                </div>
              )}
              <div className="col-auto">
                <p className="text-14 sm:text-12">
                  {item?.city}, {item?.country}
                </p>
              </div>
            </div>

            <div className="text-14 lh-15 mt-20">
              <div className="fw-500">{item?.min_rate?.boarding_details[0]}</div>
              <div className="text-light-1">{item?.min_rate?.rooms[0]?.description}</div>
            </div>

            {item?.min_rate?.non_refundable != null && (
              item?.min_rate?.non_refundable ? (
                <span onClick={() => toggleModal(item?.min_rate)} className="text-red-2 text-12 cursor-pointer">
                  Non refundable <InfoCircle />
                </span>
              ) : (
                <div className="text-12 text-green-2 lh-15 mt-10">
                  <div className="fw-500">
                    {item?.min_rate?.cancellation_policy?.under_cancellation == false
                      ? `Free cancellation till ${formatDate(
                          item?.min_rate?.cancellation_policy?.cancel_by_date,
                          "dd MMM yyyy HH:mm"
                        )}`
                      : "Refundable"}
                    <InfoCircle onClick={() => toggleModal(item?.min_rate)} className="cursor-pointer" />
                  </div>
                  {!item?.min_rate?.cancellation_policy?.under_cancellation && (
                    <div>You can cancel later, so lock in this great price today.</div>
                  )}
                </div>
              )
            )}

            {item?.min_rate?.other_inclusions?.length > 0 && (
              <div className="row x-gap-10 y-gap-10 pt-20 sm:pt-5">
                <OtherInclusions other_inclusions={item?.min_rate?.other_inclusions} loading={isLoading} />
              </div>
            )}
          </div>

          <div className="col-md-auto text-right md:text-left">
            {item?.tripadvisor_data?.num_reviews > 0 && (
              <div className="row x-gap-10 y-gap-10 justify-end items-center md:justify-start">
                <div className="col-auto">
                  {item?.tripadvisor_data?.rating_image_url && (
                    <div className="d-flex x-gap-5 items-center pt-10 sm:pt-0">
                      <Link href={item?.tripadvisor_data?.web_url} target="_blank">
                        <Image
                          src={item?.tripadvisor_data?.rating_image_url}
                          alt="Rating image"
                          width={100}
                          height={100}
                        />
                      </Link>
                    </div>
                  )}
                  <div className="text-14 lh-14 text-light-1">
                    <Link href={item?.tripadvisor_data?.web_url} target="_blank">
                      {item?.tripadvisor_data?.num_reviews} reviews
                    </Link>
                  </div>
                </div>
                <div className="col-auto">
                  <div className="flex-center text-white fw-600 text-14 size-40 rounded-4 bg-blue-1">
                    {item?.ratings || item?.tripadvisor_data?.rating}
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="text-14 text-light-1 mt-50 md:mt-20">{roomSummary}</div>
              <div className="text-22 lh-12 fw-600 mt-5">
                {item?.min_rate?.currency} {item?.min_rate?.priceWithMarkup}
              </div>
              <div className="text-14 text-light-1 mt-5">Including taxes and charges</div>
              {/* <button
                onClick={() => redirectUrl(item?.hotel_code, item?.search_id)}
                type="button"
                className="button -md -dark-1 bg-blue-1 text-white mt-24 col-12"
              >
                See Availability
              </button> */}
              <Link target="_blank" onClick={() => handleHotelClick(item)} href={`${redirectUrl(item?.hotel_code, item?.search_id)}`} className="button -md -dark-1 bg-blue-1 text-white mt-24 col-12">See Availability</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  ), [toggleModal, redirectUrl, roomSummary, isLoading]);

  // Render skeleton cards when loading or no data
  if (isLoading) {
    return (
      <>
        {Array.from({ length: 9 }).map((_, index) => (
          <SkeletonCard key={index} />
        ))}
      </>
    );
  }

  return (
    <>
     {modalData && (
        <Suspense fallback={<div>Loading...</div>}>
          <ModalComponent isOpen={modal} toggle={() => toggleModal()} fade={false}>
          <ModalHeader toggle={() => toggleModal()}>
            Cancellation Policy
          </ModalHeader>
            <div className="modal-body">
              {modalData?.non_refundable ? (
                "This booking is non-refundable and cannot be amended or modified. If you fail to arrive, no refund will be given."
              ) : (
                <Suspense fallback={<Skeleton width={300} height={20} />}>
                  <CancellationPolicy data={modalData} />
                </Suspense>
              )}
            </div>
            <div className="modal-footer">
              <Button color="primary" onClick={() => toggleModal()} className="button h-50 px-24 -dark-1 bg-blue-1 text-white">
                Close
              </Button>
            </div>
          </ModalComponent>
        </Suspense>
      )}
      {/* {JSON.stringify(response)} */}
      {/* {JSON.stringify(response[0]?.address)} */}
      {response?.length > 0 ? response?.map(renderHotelItem):  (
        <>
          {/* no_availability === true means the supplier genuinely reported
              no rooms for these criteria. An empty list WITHOUT that flag
              means the live-rates lookup failed transiently (backend code
              1599) - telling the user to "re-check location and dates" for
              that case sent them chasing a problem that wasn't theirs. */}
          {no_availability == true ? (
            <div className="text-center">
              <p className="text-16 sm:text-12 fw-600 text-dark-1 mt-20">
                No hotels available with selected criteria for {payloadFromQuery?.location?.name}
              </p>
              <p className="text-14 sm:text-12 text-light-1 mt-20">
                Please re-check your location and dates
              </p>
            </div>
          ) : response?.length == 0 && (
            <div className="text-center">
              <p className="text-16 sm:text-12 fw-600 text-dark-1 mt-20">
                We couldn&apos;t load live rates for {payloadFromQuery?.location?.name} just now
              </p>
              <p className="text-14 sm:text-12 text-light-1 mt-20">
                This is usually temporary — please try searching again in a moment
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};

HotelCard.displayName = 'HotelCard';